import { useState } from 'react';
import { X, Sparkles, Loader2, Camera, Scissors } from 'lucide-react';
import { api } from '../lib/api';
import { useI18n } from '../i18n';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { fileToCompressedDataURL } from '../lib/image';
import ImagePicker from './ImagePicker';

export default function PropagationModal({ open, onClose, plant }) {
    const { t, locale } = useI18n();
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const [imagePreview, setImagePreview] = useState(null);

    if (!open || !plant) return null;

    const onPick = async (f) => {
        if (!f) return;
        try {
            const compressed = await fileToCompressedDataURL(f);
            setImagePreview(compressed);
        } catch {
            const r = new FileReader();
            r.onload = () => setImagePreview(r.result);
            r.readAsDataURL(f);
        }
    };

    const generate = async () => {
        if (user?.is_guest) {
            toast.error('Opret en gratis profil for at bruge AI-formering 🌱');
            return;
        }
        setBusy(true);
        try {
            const r = await api.post('/ai/propagate', {
                plant_name: plant.name,
                species: plant.species || null,
                image_base64: imagePreview,
                locale,
            });
            setData(r.data);
        } catch (err) {
            const d = err?.response?.data?.detail;
            const st = err?.response?.status;
            if (st !== 401 && st !== 403) toast.error(typeof d === 'string' ? d : 'AI failed');
        } finally { setBusy(false); }
    };

    return (
        <div data-testid="propagate-modal" className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-brand-ink/40 p-0 sm:p-4 backdrop-blur-sm" onClick={onClose}>
            <div className="bg-brand-bg w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl border border-brand-border max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="sticky top-0 bg-brand-bg flex items-center justify-between px-5 py-4 border-b border-brand-border z-10">
                    <div>
                        <p className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3 inline-flex items-center gap-1"><Sparkles size={11} /> AI</p>
                        <h3 className="font-serif text-xl text-brand-ink">Formering · {plant.name}</h3>
                    </div>
                    <button data-testid="btn-close-propagate" onClick={onClose} className="p-2 hover:bg-brand-bg2 rounded-full">
                        <X size={18} />
                    </button>
                </div>

                <div className="p-5 space-y-4">
                    {!data && (
                        <>
                            <p className="text-sm text-brand-ink2 leading-relaxed">
                                AI generates a complete guide on how and when you can take cuttings, save seeds/pits, and optionally graft from this plant.
                            </p>
                            {imagePreview ? (
                                <div className="relative">
                                    <img src={imagePreview} alt="" className="w-full max-h-56 object-cover rounded-2xl border border-brand-border" />
                                    <button onClick={() => setImagePreview(null)} className="absolute top-2 right-2 bg-brand-ink/70 text-white p-1.5 rounded-full"><X size={12} /></button>
                                </div>
                            ) : (
                                <ImagePicker testId="propagate-pick" onSelect={onPick}>
                                    {(open2) => (
                                        <button type="button" onClick={open2} className="block w-full bg-brand-card border-2 border-dashed border-brand-border rounded-2xl p-6 text-center cursor-pointer hover:border-brand-green">
                                            <Camera size={24} strokeWidth={1.5} className="mx-auto text-brand-green mb-2" />
                                            <div className="text-xs text-brand-ink2">Optional: take a photo of stem/leaf for more precise advice</div>
                                        </button>
                                    )}
                                </ImagePicker>
                            )}
                            <button data-testid="btn-generate-propagate" onClick={generate} disabled={busy} className="w-full bg-brand-green hover:bg-brand-greenDark text-white py-3 rounded-full font-medium inline-flex items-center justify-center gap-2 disabled:opacity-50">
                                {busy ? <><Loader2 size={15} className="animate-spin" /> Analyzing…</> : <><Sparkles size={15} /> Generate guide</>}
                            </button>
                        </>
                    )}

                    {data?.cuttings && (
                        <div className="bg-brand-card border border-brand-border rounded-2xl p-4">
                            <div className="flex items-center gap-2 mb-2">
                                <Scissors size={16} className="text-brand-green" strokeWidth={1.8} />
                                <h4 className="font-serif text-lg text-brand-ink">Cuttings</h4>
                                {data.difficulty && <span className="ml-auto text-[10px] uppercase tracking-widest font-semibold bg-brand-bg2 px-2 py-0.5 rounded-full text-brand-ink2">{data.difficulty}</span>}
                            </div>
                            <p className="text-xs text-brand-ink3 mb-1"><strong>Best time:</strong> {data.cuttings.best_time}</p>
                            <p className="text-sm text-brand-ink2 leading-relaxed whitespace-pre-wrap mt-2">{data.cuttings.method}</p>
                            {data.cuttings.rooting_days && <p className="text-xs text-brand-ink3 mt-2">Rooting time: ~{data.cuttings.rooting_days} days</p>}
                            {data.cuttings.tips?.length > 0 && (
                                <ul className="mt-2 space-y-1">
                                    {data.cuttings.tips.map((t, i) => <li key={`cut-tip-${i}-${String(t).slice(0,15)}`} className="text-xs text-brand-ink2">💡 {t}</li>)}
                                </ul>
                            )}
                        </div>
                    )}

                    {data?.seeds && (
                        <div className="bg-brand-card border border-brand-border rounded-2xl p-4">
                            <div className="flex items-center gap-2 mb-2">
                                <span className="text-base">🌱</span>
                                <h4 className="font-serif text-lg text-brand-ink">Seeds &amp; pits</h4>
                            </div>
                            <p className="text-xs text-brand-ink3 mb-1"><strong>Harvest:</strong> {data.seeds.harvest_time}</p>
                            <p className="text-sm text-brand-ink2 leading-relaxed mt-2">{data.seeds.method}</p>
                            <p className="text-xs text-brand-ink3 mt-2"><strong>Storage:</strong> {data.seeds.storage}</p>
                            {data.seeds.viability_years && <p className="text-xs text-brand-ink3 mt-1">Viability: ~{data.seeds.viability_years} years</p>}
                        </div>
                    )}

                    {data?.grafting && (
                        <div className="bg-brand-card border border-brand-border rounded-2xl p-4">
                            <h4 className="font-serif text-lg text-brand-ink mb-2">🌿 Grafting</h4>
                            <p className="text-sm text-brand-ink2 leading-relaxed">{data.grafting}</p>
                        </div>
                    )}

                    {data?.raw && (
                        <div className="bg-brand-card border border-brand-border rounded-2xl p-4">
                            <p className="text-sm text-brand-ink2 whitespace-pre-wrap">{data.raw}</p>
                        </div>
                    )}

                    {data && (
                        <button data-testid="btn-regen-propagate" onClick={() => { setData(null); setImagePreview(null); }} className="w-full bg-brand-bg2 hover:bg-brand-border text-brand-ink py-3 rounded-full text-sm font-medium">
                            Generate again
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
